import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter as Router } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import TagManager from "react-gtm-module";

import Loader from "src/components/loader/Loader";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import { getStrings } from "src/redux/slices/appSlice/AppApi";
import "./App.css";
import AppRoutes from "src/navigation/AppRoutes";
import { Popup } from "src/components";
import {
  setBusinessUnit,
} from "src/redux/slices/appSlice/AppSlice";
import { CustomLayout } from "src/components/uIKit/CustomLayout/CustomLayout";
import { ScreenReaderProvider } from "src/hooks/useScreenReaderStatus";
import { useSingleTab } from "src/hooks/useSingleTab";
import { BusinessUnit, URL_SOURCE_TYPE } from "src/utils/enums";
import { __DEV__, sentPostMessage } from "src/utils/utils";
import { MOCK_GET_STRINGS_RESPONSE } from "src/utils/mockResponses";
import { PostMessageData } from "src/utils/types";

const App: React.FC = () => {

  const { popupInfo } = useAppSelector((state) => state.popup);
  const { isLoading, mockDataEnabled } = useAppSelector(
    (state) => state.app
  );


  const dispatch = useAppDispatch();
  const GOOGLE_TAG_ID = process.env.REACT_APP_GOOGLE_TAG_ID as string;

  // Enforce single-tab behavior
  useSingleTab();

  useEffect(() => {
    const fullUrl = window.location.href.toLowerCase();
    const isDigitalAgent = fullUrl.includes(URL_SOURCE_TYPE.DIGITAL_AGENT);
    const isDigital = fullUrl.includes(URL_SOURCE_TYPE.DIGITAL) && !isDigitalAgent;
    if (isDigitalAgent || isDigital) {
      sentPostMessage({
        message: "IFRAME_READY",
      } as PostMessageData);
    }
  }, []);

  useEffect(() => {
    if (GOOGLE_TAG_ID) {
      try {
        TagManager.initialize({
          gtmId: GOOGLE_TAG_ID,
          events: {
            sendUserInfo: "sendUserInfo",
          },
          dataLayer: {
            // Initialize with default values if needed
            userId: null,
            userProject: null,
          },
        });
      } catch (error) {
        __DEV__ && console.error("Error initializing Google Tag Manager:", error);
      }
    }
  }, [GOOGLE_TAG_ID]);



  useEffect(() => {

    getLabels();
    // check if there a businessUnit paramether on url
    const businessUnit = new URLSearchParams(window.location.search).get(
      "businessUnit"
    );
    if (businessUnit) {
      dispatch(setBusinessUnit(businessUnit as BusinessUnit));
    }
  }, []);

  const getLabels = () => {
    dispatch(getStrings());
  };

  return (
    <ScreenReaderProvider>
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <div className="error-boundary">{error.message}</div>
        )}
      >
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <CustomLayout>
            <Popup popupInfo={popupInfo} />
            {isLoading && <Loader fullHeight={true} />}
            <AppRoutes />
          </CustomLayout>
        </Router>
      </ErrorBoundary>
    </ScreenReaderProvider>
  );
};

export default App;
